import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Spinner, Card, Nav, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { dsoApi } from '../../services/api';
import { FaSignature, FaFileContract, FaIdCard, FaBuilding, FaCheck, FaSignOutAlt, FaFilePdf } from 'react-icons/fa';
import useAuth from '../../hooks/useAuth';
import ServiceAgreement from '../../components/ServiceAgreement';
import './Dashboard.css'; // Reusing dashboard styles

const styles = {
  container: {
    paddingTop: '3rem',
    paddingBottom: '3rem',
    backgroundColor: '#f8fafc', // Light background for the page
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
  },
  card: {
    border: 'none',
    borderRadius: '1rem', // Slightly larger radius
    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)', // Softer shadow
    overflow: 'hidden', // Ensure header radius is clipped
  },
  header: {
    background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', // Deeper blue gradient
    borderRadius: '1rem 1rem 0 0', // Match card radius
    padding: '2rem', // More padding
    color: 'white',
  },
  progressBar: {
    height: '10px',
    borderRadius: '5px',
    backgroundColor: 'rgba(255, 255, 255, 0.2)', // Slightly more visible background
    overflow: 'hidden', // Ensure fill stays within bounds
  },
  progressBarFill: {
    backgroundColor: '#ffffff', // Solid white fill
    height: '100%', // Fill height
    borderRadius: '5px',
    transition: 'width 0.4s ease-in-out', // Smoother transition
  },
  navTabs: {
    borderBottom: 'none', // Remove default border
    marginTop: '1.5rem',
    display: 'flex',
    flexWrap: 'wrap', // Allow wrapping on smaller screens
    gap: '0.5rem',
  },
  navLink: {
    color: 'rgba(255, 255, 255, 0.8)',
    border: 'none',
    padding: '0.75rem 1.25rem',
    borderRadius: '0.5rem', // Consistent radius
    fontWeight: '500',
    transition: 'all 0.2s ease',
    backgroundColor: 'transparent', // Ensure default is transparent
    textAlign: 'center', // Center text
    flexGrow: 1, // Allow tabs to grow
    '&:hover': {
      color: '#ffffff',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
    '&.disabled': { // Style for disabled tabs
       opacity: 0.6,
       cursor: 'not-allowed',
       backgroundColor: 'transparent', // Ensure no background on disabled hover
       '&:hover': { // Prevent hover effect on disabled
         backgroundColor: 'transparent',
         color: 'rgba(255, 255, 255, 0.8)',
       }
    }
  },
  navLinkActive: { // Refined active nav link
    color: '#1e3a8a !important', // Darker text for contrast
    backgroundColor: '#ffffff !important', // Solid white background
    fontWeight: '600',
  },
  cardBody: { // More padding in body
    padding: '2rem',
  },
  documentCard: { // Style for individual document upload cards
    border: '1px solid #e2e8f0',
    borderRadius: '0.75rem',
    transition: 'all 0.2s ease',
    marginBottom: '1rem', // Add space below each card
    backgroundColor: '#ffffff', // Ensure white background
  },
  documentCardUploaded: { // Style when document is uploaded
    borderColor: '#10b981', // Green border
    backgroundColor: '#f0fdf4', // Light green background
  },
  statusLabel: { // Base style for status labels
    fontWeight: '500',
    padding: '0.25rem 0.5rem',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    display: 'inline-flex', // Align icon and text
    alignItems: 'center',
    gap: '0.3rem', // Space between icon and text
  },
  statusCompleted: { // Style for completed status
    color: '#059669',
    backgroundColor: '#d1fae5',
  },
  statusPending: { // Style for pending status
    color: '#d97706',
    backgroundColor: '#fef3c7',
  },
  button: { // Base button style (can be applied selectively)
    padding: '0.8rem 1.6rem',
    fontSize: '1rem',
    borderRadius: '0.5rem',
    fontWeight: '500',
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
    }
  }
};

const Onboarding = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState('documents');
  const [documentFiles, setDocumentFiles] = useState({
    adhaar_front: null,
    adhaar_back: null,
    pan_card: null,
    company_pan_card: null,
    company_certificate: null
  });
  const [documentUploadStatus, setDocumentUploadStatus] = useState({
    adhaar_front: false,
    adhaar_back: false,
    pan_card: false,
    company_pan_card: false,
    company_certificate: false
  });
  const navigate = useNavigate();
  const auth = useAuth(); // Initialize auth hook
  
  useEffect(() => {
    document.title = 'DSA Onboarding';
    
    const fetchProfile = async () => {
      try {
        const response = await dsoApi.getDsoProfile();
        setProfile(response.data);
        
        const dsoProfile = response.data.dso_profile;
        const isBusinessProfile = dsoProfile.applicant_type === 'Business';
        
        // Check if all conditions are met to redirect to dashboard
        const isVerified = !!dsoProfile.is_verified;
        const isAgreementSigned = !!dsoProfile.agreement_signed;
        
        // Check documents based on profile type
        let areDocsComplete = false;
        if (isBusinessProfile) {
          areDocsComplete = !!dsoProfile.company_pan_card_file && !!dsoProfile.company_certificate_file;
        } else {
          areDocsComplete = !!dsoProfile.adhaar_card_front_file && !!dsoProfile.adhaar_card_back_file && !!dsoProfile.pan_card_file;
        }
        
        // If all conditions are met, redirect to dashboard
        if (isVerified && isAgreementSigned && areDocsComplete) {
          navigate('/dso/home');
          return; // Stop further execution of this function
        }
        
        // Otherwise, continue with onboarding
        determineInitialTab(dsoProfile);
        updateDocumentStatus(dsoProfile);
        setAgreed(isAgreementSigned);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching DSO profile:", err);
        setError("Failed to load your profile information. Please try again later.");
        setLoading(false);
      }
    };
    
    fetchProfile();
  }, [navigate]);
  
  // Determine which tab should be active initially
  const determineInitialTab = (dsoProfile) => {
    const isBusinessProfile = dsoProfile?.applicant_type === 'Business';
    
    // For business profiles, check business documents
    if (isBusinessProfile) {
      if (!dsoProfile.company_pan_card_file || !dsoProfile.company_certificate_file) {
        setActiveTab('documents');
      } 
      else if (!dsoProfile.agreement_signed) {
        setActiveTab('agreement');
      }
      else if (!dsoProfile.is_verified) {
        setActiveTab('verification');
      }
      // If all steps completed, it will default to showing the current tab
    }
    // For individual profiles, check personal documents
    else {
      if (!dsoProfile.adhaar_card_front_file || !dsoProfile.pan_card_file) {
        setActiveTab('documents');
      } 
      else if (!dsoProfile.agreement_signed) {
        setActiveTab('agreement');
      }
      else if (!dsoProfile.is_verified) {
        setActiveTab('verification');
      }
    }
  };
  
  // Update document status based on profile
  const updateDocumentStatus = (dsoProfile) => {
    if (!dsoProfile) {
      console.error("updateDocumentStatus called with null or undefined dsoProfile");
      return;
    }
    
    console.log("Profile data for document status:", {
      adhaar_front: dsoProfile.adhaar_card_front_file,
      adhaar_back: dsoProfile.adhaar_card_back_file,
      pan_card: dsoProfile.pan_card_file,
      company_pan_card: dsoProfile.company_pan_card_file,
      company_certificate: dsoProfile.company_certificate_file
    });
    
    const updatedStatus = {
      adhaar_front: !!dsoProfile.adhaar_card_front_file,
      adhaar_back: !!dsoProfile.adhaar_card_back_file,
      pan_card: !!dsoProfile.pan_card_file,
      company_pan_card: !!dsoProfile.company_pan_card_file,
      company_certificate: !!dsoProfile.company_certificate_file
    };
    
    console.log("Updated document status:", updatedStatus);
    setDocumentUploadStatus(updatedStatus);
  };
  
  const handlePrintServiceAgreement = () => {
    // Create a hidden iframe to avoid affecting the current page layout
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'absolute';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    document.body.appendChild(printFrame);
    
    // Get the iframe's document and write the agreement content
    const frameDoc = printFrame.contentDocument || printFrame.contentWindow.document;
    frameDoc.open();
    
    // Get the agreement content
    const agreementContent = document.querySelector('.service-agreement').innerHTML;
    const companyName = profile?.name || profile?.dso_profile?.company_name || "Company";
    
    // Write the document with the agreement content
    frameDoc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Service Agreement - ${companyName}</title>
          <style>
            body { 
              font-family: 'Calibri', sans-serif;
              padding: 40px; 
            }
            h1 { 
              font-size: 18px; 
              font-weight: bold; 
              text-align: center;
            }
            h2 { 
              font-size: 16px; 
              font-weight: bold; 
              margin-top: 16px;
            }
            p { 
              margin-bottom: 10px; 
              text-align: justify;
            }
            .centered { 
              text-align: center; 
            }
            .bold { 
              font-weight: bold; 
            }
            .underline { 
              text-decoration: underline; 
            }
            .section { 
              margin-bottom: 20px; 
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 20px 0;
            }
            table th, table td {
              border: 1px solid #000;
              padding: 8px 12px;
              text-align: left;
            }
            .signature-section {
              display: flex;
              justify-content: space-between;
              margin-top: 40px;
              page-break-inside: avoid;
            }
            .signature-block {
              width: 45%;
              border: 1px solid #000;
            }
            .signature-title {
              border-bottom: 1px solid #000;
              padding: 10px;
              font-weight: bold;
            }
            .signature-space {
              height: 80px;
              border-bottom: 1px solid #000;
            }
            .signature-info {
              padding: 10px;
            }
            @page { 
              size: A4; 
              margin: 2cm; 
            }
          </style>
        </head>
        <body>
          <div>
            ${agreementContent}
          </div>
        </body>
      </html>
    `);
    frameDoc.close();
    
    // Wait for the content to load and then print
    setTimeout(() => {
      printFrame.contentWindow.focus();
      printFrame.contentWindow.print();
      
      // Remove the iframe after printing or after some time
      setTimeout(() => {
        document.body.removeChild(printFrame);
      }, 1000);
    }, 500);
  };

  const handleSignAgreement = async () => {
    if (!agreed) {
      setError("Please check the agreement box before proceeding");
      return;
    }
    
    try {
      setSigning(true);
      await dsoApi.signAgreement();
      
      // Update profile after signing
      const response = await dsoApi.getDsoProfile();
      setProfile(response.data);
      
      // If all steps are completed, redirect to dashboard
      if (response.data.dso_profile.is_verified) {
        navigate('/dso/home', { replace: true });
      } else {
        // Otherwise, move to the verification tab
        setActiveTab('verification');
      }
      
      setSigning(false);
    } catch (err) {
      console.error("Error signing agreement:", err);
      setError("Failed to sign agreement. Please try again later.");
      setSigning(false);
    }
  };
  
  const handleFileChange = (e, docType) => {
    setDocumentFiles({
      ...documentFiles,
      [docType]: e.target.files[0]
    });
  };
  
  const handleDocumentUpload = async (docType) => {
    if (!documentFiles[docType]) {
      setError(`Please select a file for ${docType.replace('_', ' ')}`);
      return;
    }
    
    const formData = new FormData();
    formData.append('file', documentFiles[docType]);
    formData.append('document_type', docType);
    
    try {
      setSaving(true);
      console.log(`Uploading ${docType} document...`);

      // --- CHANGE THIS LINE ---
      // Use the correct API function pointing to /dso/documents
      const uploadResponse = await dsoApi.uploadDsoDocument(formData);
      // --- END OF CHANGE ---

      console.log("Upload response:", uploadResponse.data);

      // --- MODIFY SUCCESS CHECK (Optional but Recommended) ---
      // Check for a specific success indicator or rely on HTTP status
      // Assuming the backend returns the updated profile or a success flag on 2xx status
      if (uploadResponse.status >= 200 && uploadResponse.status < 300 && uploadResponse.data) {
      // --- END OF MODIFICATION ---

        console.log(`${docType} uploaded successfully!`);

        // Refresh profile data
        const profileResponse = await dsoApi.getDsoProfile();
        setProfile(profileResponse.data);
        
        // Use the existing updateDocumentStatus function to update status
        updateDocumentStatus(profileResponse.data.dso_profile);

        // Clear the selected file after successful upload
        setDocumentFiles(prev => ({ ...prev, [docType]: null }));
        setError(null); // Clear any previous errors

      } else {
        // Handle cases where the API call succeeded (2xx) but didn't return expected data
        // or returned an explicit failure message within the data.
        const errorMsg = uploadResponse.data?.detail || uploadResponse.data?.message || "Document upload request completed, but failed server-side.";
        console.error("Upload failed:", errorMsg, uploadResponse);
        setError(errorMsg);
      }

      setSaving(false);
    } catch (err) {
       console.error(`Error uploading ${docType}:`, err);
       // Check if the error response contains specific details
       let errorMsg;
       if (err.response?.data?.detail) {
         // Make sure we convert the error detail to a string if it's an object
         errorMsg = typeof err.response.data.detail === 'object' 
           ? JSON.stringify(err.response.data.detail) 
           : String(err.response.data.detail);
       } else {
         errorMsg = `Failed to upload ${docType.replace('_', ' ')}. Please try again.`;
       }
       setError(errorMsg);
       setSaving(false);
    }
  };
  
  // Check if all required documents are uploaded
  const areRequiredDocumentsUploaded = () => {
    const isBusinessProfile = profile?.dso_profile?.applicant_type === 'Business';

    if (isBusinessProfile) {
      // --- MODIFY THIS RETURN STATEMENT ---
      return documentUploadStatus.company_pan_card &&
             documentUploadStatus.company_certificate; // Remove the check for personal pan_card here
      // --- END MODIFICATION ---
    } else {
      // Individual profile requirements remain the same
      return documentUploadStatus.adhaar_front &&
             documentUploadStatus.adhaar_back &&
             documentUploadStatus.pan_card;
    }
  };
  
  // Helper function to get status label
  const getStatusLabel = (isCompleted) => {
    const statusStyle = isCompleted ? styles.statusCompleted : styles.statusPending;
    const text = isCompleted ? 'Completed' : 'Pending';
    const icon = isCompleted ? <FaCheck size={14} /> : null; // Only show check for completed

    return (
      <span style={{ ...styles.statusLabel, ...statusStyle }}>
        {icon}
        {text}
      </span>
    );
  };

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" />
        <p>Loading onboarding information...</p>
      </Container>
    );
  }

  return (
    <div style={styles.container}>
      <Container className="py-4">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card style={styles.card}>
              <div style={styles.header}> {/* Removed text-white as color is set in style */}
                <div className="d-flex justify-content-between align-items-center">
                  <h3 className="mb-3 fw-bold">DSA Onboarding</h3>
                  <Button 
                    variant="outline-light" 
                    size="sm" 
                    onClick={() => {
                      auth.logout();
                      navigate('/');
                    }}
                  >
                    <FaSignOutAlt className="me-1" /> Logout
                  </Button>
                </div>
                <p className="mb-4 opacity-75">Complete the steps below to activate your account.</p> {/* Added subtitle */}
                <div className="progress-wrapper mb-3">
                  <div style={styles.progressBar}>
                    <div
                      style={{
                        ...styles.progressBarFill,
                        width: `${activeTab === 'documents' ? '33%' :
                                activeTab === 'agreement' ? '66%' : '100%'}`
                      }}
                      role="progressbar"
                      aria-valuenow={activeTab === 'documents' ? 33 : activeTab === 'agreement' ? 66 : 100}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    />
                  </div>
                </div>
                <Nav style={styles.navTabs} variant="tabs" activeKey={activeTab}>
                  <Nav.Item style={{ flexBasis: '30%', minWidth: '120px' }}> {/* Adjust flexBasis as needed */}
                    <Nav.Link
                      eventKey="documents"
                      onClick={() => setActiveTab('documents')}
                      style={{
                        ...styles.navLink,
                        ...(activeTab === 'documents' ? styles.navLinkActive : {})
                      }}
                      className={activeTab === 'documents' ? 'active' : ''} // Add active class for potential Bootstrap overrides
                    >
                      Documents
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item style={{ flexBasis: '30%', minWidth: '120px' }}>
                    <Nav.Link
                      eventKey="agreement"
                      onClick={() => !areRequiredDocumentsUploaded() ? null : setActiveTab('agreement')}
                      style={{
                        ...styles.navLink,
                        ...(activeTab === 'agreement' ? styles.navLinkActive : {}),
                        ...(!areRequiredDocumentsUploaded() ? styles.navLink['&.disabled'] : {}) // Apply disabled style directly
                      }}
                      disabled={!areRequiredDocumentsUploaded()}
                      className={`${activeTab === 'agreement' ? 'active' : ''} ${!areRequiredDocumentsUploaded() ? 'disabled' : ''}`}
                    >
                      Agreement
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item style={{ flexBasis: '30%', minWidth: '120px' }}>
                    <Nav.Link
                      eventKey="verification"
                      onClick={() => !profile?.dso_profile?.agreement_signed ? null : setActiveTab('verification')}
                      style={{
                        ...styles.navLink,
                        ...(activeTab === 'verification' ? styles.navLinkActive : {}),
                        ...(!profile?.dso_profile?.agreement_signed ? styles.navLink['&.disabled'] : {}) // Apply disabled style directly
                      }}
                      disabled={!profile?.dso_profile?.agreement_signed}
                      className={`${activeTab === 'verification' ? 'active' : ''} ${!profile?.dso_profile?.agreement_signed ? 'disabled' : ''}`}
                    >
                      Verification
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </div>

              <Card.Body style={styles.cardBody}> {/* Apply explicit padding */}
                {error && (
                  <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                  </Alert>
                )}
                
                {/* Documents Tab */}
                {activeTab === 'documents' && (
                  <div>
                    <h4 className="mb-4">Upload Required Documents</h4>
                    <p className="text-muted">Please upload the following documents to proceed with your onboarding.</p>

                    {profile?.dso_profile?.applicant_type !== 'Business' && (
                      <div className="mb-4">
                        <h5 className="mb-3">Personal Documents</h5>
                        <Row>
                          <Col md={6} className="mb-3">
                            <Card style={{ ...styles.documentCard, ...(documentUploadStatus.adhaar_front ? styles.documentCardUploaded : {}) }}>
                              <Card.Body className="p-3"> {/* Slightly less padding inside doc cards */}
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <div className="d-flex align-items-center">
                                    <FaIdCard size={20} className="me-2 text-primary opacity-75" />
                                    <strong className="fw-medium">Aadhaar Card (Front)</strong>
                                  </div>
                                  {getStatusLabel(documentUploadStatus.adhaar_front)}
                                </div>
                                <div className="mt-3">
                                  {!documentUploadStatus.adhaar_front ? (
                                    <>
                                      <Form.Control
                                        type="file"
                                        accept="image/*, application/pdf"
                                        onChange={(e) => handleFileChange(e, 'adhaar_front')}
                                        disabled={saving}
                                        size="sm"
                                      />
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        className="mt-2"
                                        onClick={() => handleDocumentUpload('adhaar_front')}
                                        disabled={!documentFiles.adhaar_front || saving}
                                        style={{ minWidth: '80px' }} // Ensure button has min width
                                      >
                                        {saving ? <Spinner size="sm" animation="border" /> : 'Upload'}
                                      </Button>
                                    </>
                                  ) : (
                                    <div className="text-success d-flex align-items-center mt-2">
                                      <FaCheck size={16} className="me-2" />
                                      <span>Document Uploaded</span>
                                    </div>
                                  )}
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>

                          <Col md={6} className="mb-3">
                            <Card style={{ ...styles.documentCard, ...(documentUploadStatus.adhaar_back ? styles.documentCardUploaded : {}) }}>
                              <Card.Body className="p-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <div className="d-flex align-items-center">
                                    <FaIdCard size={20} className="me-2 text-primary opacity-75" />
                                    <strong className="fw-medium">Aadhaar Card (Back)</strong>
                                  </div>
                                  {getStatusLabel(documentUploadStatus.adhaar_back)}
                                </div>
                                <div className="mt-3">
                                  {!documentUploadStatus.adhaar_back ? (
                                    <>
                                      <Form.Control
                                        type="file"
                                        accept="image/*, application/pdf"
                                        onChange={(e) => handleFileChange(e, 'adhaar_back')}
                                        disabled={saving}
                                        size="sm"
                                      />
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        className="mt-2"
                                        onClick={() => handleDocumentUpload('adhaar_back')}
                                        disabled={!documentFiles.adhaar_back || saving}
                                        style={{ minWidth: '80px' }}
                                      >
                                        {saving ? <Spinner size="sm" animation="border" /> : 'Upload'}
                                      </Button>
                                    </>
                                  ) : (
                                    <div className="text-success d-flex align-items-center mt-2">
                                      <FaCheck size={16} className="me-2" />
                                      <span>Document Uploaded</span>
                                    </div>
                                  )}
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={6} className="mb-3">
                             <Card style={{ ...styles.documentCard, ...(documentUploadStatus.pan_card ? styles.documentCardUploaded : {}) }}>
                               <Card.Body className="p-3">
                                 <div className="d-flex justify-content-between align-items-center mb-2">
                                   <div className="d-flex align-items-center">
                                     <FaIdCard size={20} className="me-2 text-primary opacity-75" />
                                     <strong className="fw-medium">PAN Card</strong>
                                   </div>
                                   {getStatusLabel(documentUploadStatus.pan_card)}
                                 </div>
                                 <div className="mt-3">
                                   {!documentUploadStatus.pan_card ? (
                                     <>
                                       <Form.Control
                                         type="file"
                                         accept="image/*, application/pdf"
                                         onChange={(e) => handleFileChange(e, 'pan_card')}
                                         disabled={saving}
                                         size="sm"
                                       />
                                       <Button
                                         variant="outline-primary"
                                         size="sm"
                                         className="mt-2"
                                         onClick={() => handleDocumentUpload('pan_card')}
                                         disabled={!documentFiles.pan_card || saving}
                                         style={{ minWidth: '80px' }}
                                       >
                                         {saving ? <Spinner size="sm" animation="border" /> : 'Upload'}
                                       </Button>
                                     </>
                                   ) : (
                                     <div className="text-success d-flex align-items-center mt-2">
                                       <FaCheck size={16} className="me-2" />
                                       <span>Document Uploaded</span>
                                     </div>
                                   )}
                                 </div>
                               </Card.Body>
                             </Card>
                          </Col>
                        </Row>
                      </div>
                    )}

                    {/* Business Documents - Only show if applicant_type is Business */}
                    {profile?.dso_profile?.applicant_type === 'Business' && (
                      <div className="mb-4">
                        <h5 className="mb-3">Business Documents</h5>
                        <Row>
                          <Col md={6} className="mb-3">
                            <Card style={{ ...styles.documentCard, ...(documentUploadStatus.company_pan_card ? styles.documentCardUploaded : {}) }}>
                              <Card.Body className="p-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <div className="d-flex align-items-center">
                                    <FaBuilding size={20} className="me-2 text-primary opacity-75" />
                                    <strong className="fw-medium">Company PAN Card</strong>
                                  </div>
                                  {getStatusLabel(documentUploadStatus.company_pan_card)}
                                </div>
                                <div className="mt-3">
                                  {!documentUploadStatus.company_pan_card ? (
                                    <>
                                      <Form.Control
                                        type="file"
                                        accept="image/*, application/pdf"
                                        onChange={(e) => handleFileChange(e, 'company_pan_card')}
                                        disabled={saving}
                                        size="sm"
                                      />
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        className="mt-2"
                                        onClick={() => handleDocumentUpload('company_pan_card')}
                                        disabled={!documentFiles.company_pan_card || saving}
                                        style={{ minWidth: '80px' }}
                                      >
                                        {saving ? <Spinner size="sm" animation="border" /> : 'Upload'}
                                      </Button>
                                    </>
                                  ) : (
                                    <div className="text-success d-flex align-items-center mt-2">
                                      <FaCheck size={16} className="me-2" />
                                      <span>Document Uploaded</span>
                                    </div>
                                  )}
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>

                          <Col md={6} className="mb-3">
                            <Card style={{ ...styles.documentCard, ...(documentUploadStatus.company_certificate ? styles.documentCardUploaded : {}) }}>
                              <Card.Body className="p-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <div className="d-flex align-items-center">
                                    <FaBuilding size={20} className="me-2 text-primary opacity-75" />
                                    <strong className="fw-medium">Company Certificate</strong>
                                  </div>
                                  {getStatusLabel(documentUploadStatus.company_certificate)}
                                </div>
                                <div className="mt-3">
                                  {!documentUploadStatus.company_certificate ? (
                                    <>
                                      <Form.Control
                                        type="file"
                                        accept="image/*, application/pdf"
                                        onChange={(e) => handleFileChange(e, 'company_certificate')}
                                        disabled={saving}
                                        size="sm"
                                      />
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        className="mt-2"
                                        onClick={() => handleDocumentUpload('company_certificate')}
                                        disabled={!documentFiles.company_certificate || saving}
                                        style={{ minWidth: '80px' }}
                                      >
                                        {saving ? <Spinner size="sm" animation="border" /> : 'Upload'}
                                      </Button>
                                    </>
                                  ) : (
                                    <div className="text-success d-flex align-items-center mt-2">
                                      <FaCheck size={16} className="me-2" />
                                      <span>Document Uploaded</span>
                                    </div>
                                  )}
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                        </Row>
                      </div>
                    )}
                    
                    <div className="d-grid gap-2 mt-4">
                      <Button 
                        variant="primary" 
                        size="lg" 
                        disabled={!areRequiredDocumentsUploaded()}
                        onClick={() => setActiveTab('agreement')}
                      >
                        Continue to Agreement
                      </Button>
                    </div>
                  </div>
                )}
                {/* Agreement Tab */}
                {activeTab === 'agreement' && (
                  <div className="mt-2"> {/* Add margin top */}
                    <div className="mb-4">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4 className="mb-0 fw-medium">Service Agreement</h4>
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={handlePrintServiceAgreement}
                        >
                          <FaFilePdf className="me-2" /> Save as PDF
                        </Button>
                      </div>
                      <p className="text-muted">Please review and sign the Service Agreement to proceed.</p>
                    </div>
                    
                    <div className="mb-4">
                      <ServiceAgreement 
                        companyName={profile?.name || profile?.dso_profile?.company_name || ""} 
                        companyAddress={profile?.dso_profile?.address || ""} 
                      />
                    </div>
                    
                    <Form>
                      <Form.Group className="mb-4">
                        <Form.Check
                          type="checkbox"
                          id="agreement-checkbox"
                          label="I have read and agree to the terms and conditions of the Service Agreement"
                          checked={agreed}
                          onChange={(e) => setAgreed(e.target.checked)}
                          className="user-select-none"
                          disabled={profile?.dso_profile?.agreement_signed}
                        />
                      </Form.Group>
                      
                      <div className="d-grid gap-2">
                        {profile?.dso_profile?.agreement_signed ? (
                          <Button 
                            variant="success"
                            size="lg"
                            disabled={true}
                            style={styles.button} // Optional: Apply consistent button style
                          >
                            <FaCheck size={18} className="me-2" />
                            Agreement Signed
                          </Button>
                        ) : (
                          <Button
                            variant="primary"
                            size="lg"
                            disabled={signing || !agreed}
                            onClick={handleSignAgreement}
                            className="sign-agreement-btn"
                            style={styles.button} // Apply button style
                          >
                            {signing ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Signing Agreement...
                              </>
                            ) : (
                              <>
                                <FaSignature size={18} className="me-2" />
                                Sign Agreement
                              </>
                            )}
                          </Button>
                        )}
                        {profile?.dso_profile?.agreement_signed && (
                          <Button
                            variant="outline-primary" // Changed to outline
                            onClick={() => setActiveTab('verification')}
                            className="mt-2"
                            // style={styles.button} // Optional: Apply consistent button style
                          >
                            Continue to Verification
                          </Button>
                        )}
                      </div>
                    </Form>
                  </div>
                )}
                {/* Verification Tab */}
                {activeTab === 'verification' && (
                  <div className="mt-2"> {/* Add margin top */}
                    <h4 className="mb-3 fw-medium">Verification Status</h4> {/* Adjusted heading */}
                    <p className="text-muted mb-4">Your documents and agreement have been submitted for review.</p> {/* Adjusted text */}

                    <Card className="mb-4 shadow-sm" style={{ borderRadius: '0.75rem' }}> {/* Add style */}
                      <Card.Body className="p-4"> {/* More padding */}
                        <h5 className="mb-3">Onboarding Progress</h5>
                        <hr className="mb-4" />

                        {/* Use a more structured layout for status items */}
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <span className="fw-medium">Document Upload</span>
                          {getStatusLabel(areRequiredDocumentsUploaded())}
                        </div>

                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <span className="fw-medium">Agreement Signing</span>
                          {getStatusLabel(!!profile?.dso_profile?.agreement_signed)}
                        </div>

                        <div className="d-flex justify-content-between align-items-center">
                          <span className="fw-medium">Admin Verification</span>
                          {getStatusLabel(!!profile?.dso_profile?.is_verified)}
                        </div>
                      </Card.Body>
                    </Card>

                    {!profile?.dso_profile?.is_verified && (
                      <Alert variant="info">
                        <Alert.Heading>Pending Verification</Alert.Heading>
                        <p>
                          Your account is currently under review by our administration team. 
                          This process typically takes 1-2 business days.
                        </p>
                        <hr />
                        <p className="mb-0">
                          Once your account is verified, you'll have full access to the DSA dashboard.
                          For any queries, please contact your Relationship Manager.
                        </p>
                      </Alert>
                    )}

                    {profile?.dso_profile?.is_verified && (
                      <div className="text-center mt-4"> {/* Add margin top */}
                        <Alert variant="success">
                          <Alert.Heading><FaCheck size={20} className="me-2" />Verification Complete!</Alert.Heading>
                          <p>Your account has been verified. You now have full access to the DSA dashboard.</p>
                        </Alert>

                        <Button
                          variant="primary"
                          size="lg"
                          onClick={() => navigate('/dso/home', { replace: true })}
                          className="mt-3"
                          style={styles.button} // Apply button style
                        >
                          Go to Dashboard
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </Card.Body>
              
              <Card.Footer className="text-muted text-center">
                <small>If you have any questions about the onboarding process, please contact your Relationship Manager.</small>
              </Card.Footer>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Onboarding;
